import {getImageUrl, getVideoUrl, Sizes, srcSet} from '@cohort/shared/utils/media';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {Dialog, Transition} from '@headlessui/react';
import {Image} from '@phosphor-icons/react';
import React, {Fragment, useCallback, useState} from 'react';

interface Props {
  digitalAssetImageFileKey: string | null;
  animationFileKey: string | null;
  disableModal?: boolean;
  hasDarkBg?: boolean;
}

export const DigitalAssetVisual: React.FC<Props> = ({
  digitalAssetImageFileKey,
  animationFileKey,
  disableModal,
  hasDarkBg,
}: Props): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const handleZoom = useCallback(() => {
    if (digitalAssetImageFileKey !== null) {
      setOpen(true);
    }
  }, [digitalAssetImageFileKey]);

  return (
    <Fragment>
      <Media
        digitalAssetImageFileKey={digitalAssetImageFileKey}
        animationFileKey={animationFileKey}
        onClick={handleZoom}
        disableModal={disableModal}
        hasDarkBg={hasDarkBg}
      />
      {disableModal !== true && (
        <Modal open={open} setOpen={setOpen}>
          <Media
            digitalAssetImageFileKey={digitalAssetImageFileKey}
            animationFileKey={animationFileKey}
            disableModal={disableModal}
            hasDarkBg={hasDarkBg}
            large
          />
        </Modal>
      )}
    </Fragment>
  );
};
DigitalAssetVisual.displayName = 'DigitalAssetVisual';

interface MediaProps {
  digitalAssetImageFileKey: string | null;
  animationFileKey: string | null;
  large?: boolean;
  onClick?: () => void;
  disableModal?: boolean;
  hasDarkBg?: boolean;
}

const Media: React.FC<MediaProps> = ({
  digitalAssetImageFileKey,
  animationFileKey,
  large,
  onClick,
  disableModal,
  hasDarkBg,
}) => {
  const style = classNames(
    'rounded-xl object-fit max-h-full max-w-full',
    hasDarkBg ? 'border-white/20 border' : 'border-black/20 border',
    onClick && disableModal !== true && 'cursor-zoom-in'
  );

  if (digitalAssetImageFileKey === null) {
    return (
      <div className={style}>
        <Image className="w-full text-gray-300" />
      </div>
    );
  }

  const size = large ? Sizes.L : Sizes.M;

  const highDef = large ? Sizes.XL : Sizes.L;

  const imageUrl = getImageUrl(import.meta.env.COHORT_ENV, digitalAssetImageFileKey, {
    h: size,
    w: size,
  });

  const highDefImageUrl = getImageUrl(import.meta.env.COHORT_ENV, digitalAssetImageFileKey, {
    h: highDef,
    w: highDef,
  });

  const image = (
    <img
      title="nft"
      className={style}
      srcSet={srcSet(`${imageUrl} 1x`, `${highDefImageUrl} 2x`)}
      src={imageUrl}
      data-testid="nft-visual-image"
      onClick={() => onClick?.()}
    />
  );

  if (animationFileKey === null) {
    return image;
  }

  return (
    <video
      controls={false}
      disableRemotePlayback
      disablePictureInPicture
      autoPlay
      playsInline
      webkit-playsinline="true"
      loop
      muted
      className={style}
      data-testid="nft-visual-video"
      onClick={() => onClick?.()}
    >
      <source src={getVideoUrl(import.meta.env.COHORT_ENV, animationFileKey, {h: size, w: size})} />
      {image}
    </video>
  );
};
Media.displayName = 'Media';

interface ModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children: JSX.Element | JSX.Element[];
}

const Modal: React.FC<ModalProps> = ({open, setOpen, children}) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50 bg-blue-300" onClose={() => setOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 flex cursor-zoom-out justify-center bg-gray-500 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="fixed inset-y-0 flex max-w-full justify-center">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Panel className="relative">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="fixed left-0 top-0 flex h-screen w-screen items-center justify-center p-4">
                      {children}
                    </div>
                  </Transition.Child>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
Modal.displayName = 'Modal';
